import { useApolloClient } from '@apollo/client';
import { AlertTriangleIcon, InfoCircleIcon, RefreshIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useBankConnectionSync } from '../../hooks/useBankConnectionSync';
import logger from '../../lib/logger';

interface IBaseSyncingNoticeProps {
  /**
   * Whether connections are actively syncing (no lastSyncedAt yet).
   */
  hasUnsyncedConnections: boolean;
  /**
   * Whether historical sync is still in progress.
   */
  hasIncompleteHistoricalSync: boolean;
  /**
   * Whether there is a potential sync issue.
   */
  hasPotentialSyncIssue: boolean;
}

/**
 * Base visual component for displaying a syncing notice.
 * Use this component directly when you need to control the syncing state manually.
 */
export const BaseSyncingNotice = ({
  hasUnsyncedConnections,
  hasIncompleteHistoricalSync,
  hasPotentialSyncIssue,
}: IBaseSyncingNoticeProps) => {
  if (!hasUnsyncedConnections && !hasIncompleteHistoricalSync && !hasPotentialSyncIssue) {
    return null;
  }

  let colorClasses: { bg: string; ring: string; text: string };
  let Icon: React.ComponentType<{ className?: string }>;
  let message: string;

  if (hasPotentialSyncIssue) {
    colorClasses = {
      bg: 'bg-utility-error-50',
      ring: 'ring-utility-error-200',
      text: 'text-utility-error-700',
    };
    Icon = AlertTriangleIcon;
    message =
      "Plaid is having difficulty sending your financial data. We're working on it and will update you by email with a solution.";
  } else if (hasUnsyncedConnections) {
    colorClasses = {
      bg: 'bg-utility-orange-50',
      ring: 'ring-utility-orange-200',
      text: 'text-utility-orange-700',
    };
    Icon = ({ className }) => <RefreshIcon className={`${className} animate-spin`} />;
    message = 'Plaid is sending your financial information.';
  } else {
    colorClasses = {
      bg: 'bg-utility-warning-50',
      ring: 'ring-utility-warning-200',
      text: 'text-utility-warning-700',
    };
    Icon = InfoCircleIcon;
    message =
      'Plaid is sending your historical transactions. This may take up to 24 hours. During that time, your product experience will be incomplete.';
  }

  return (
    <div
      className={`${colorClasses.bg} ${colorClasses.ring} m-0 flex items-center gap-2 px-6 py-5 ring-1 ring-inset md:mx-4 md:mt-3 md:rounded-xl lg:mx-8`}
    >
      <Icon className={`${colorClasses.text} size-6 shrink-0`} />
      <span className={`${colorClasses.text} text-base font-medium`}>{message}</span>
    </div>
  );
};

/**
 * Connected component that uses the useBankConnectionSync hook to determine syncing state.
 * Au
 * tomatically refreshes the Apollo client store when syncing starts.
 */
const SyncingNotice = () => {
  const { hasUnsyncedConnections, hasIncompleteHistoricalSync, hasPotentialSyncIssue } = useBankConnectionSync();
  const client = useApolloClient();
  const prevUnsyncedRef = React.useRef(hasUnsyncedConnections);
  const prevHistoricalRef = React.useRef(hasIncompleteHistoricalSync);

  // Automatically refresh store when either flag transitions from true to false
  React.useEffect(() => {
    const unsyncedStarted = prevUnsyncedRef.current && !hasUnsyncedConnections;
    const historicalStarted = prevHistoricalRef.current && !hasIncompleteHistoricalSync;

    if (unsyncedStarted || historicalStarted) {
      // Refresh store to make sure we have fresh data
      client.resetStore().catch(() => {
        logger.error('Failed to reset Apollo client store');
      });
    }

    prevUnsyncedRef.current = hasUnsyncedConnections;
    prevHistoricalRef.current = hasIncompleteHistoricalSync;
  }, [hasUnsyncedConnections, hasIncompleteHistoricalSync, client]);

  return (
    <BaseSyncingNotice
      hasUnsyncedConnections={hasUnsyncedConnections}
      hasIncompleteHistoricalSync={hasIncompleteHistoricalSync}
      hasPotentialSyncIssue={hasPotentialSyncIssue}
    />
  );
};

export default SyncingNotice;
